import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { SideBarSimpleMethodItem } from '../../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { StyledContainerItems } from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { viewRightSideBarEnum } from '../../../../constants/sidebarEnums';
import Strings from '../../../../i18n';
import { SideBarDeleteActionWithPopup } from '../../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import {
  useDeleteLocationByRoleMutation,
  useResetLocationMutation
} from '../../../../services/locations/locationsService';
import { useDeleteOrganizationMutation } from '../../../../services/device/deviceService';
import { homeListTypes } from '../../../../constants/commonEnums';
import { ILocOrgItem } from '../../../../types/location/location';
import { SideBarResetActionWithPopup } from '../../../../components/commons/SideBarComponents/ResetActionWithPopup/sideBarResetActionWithPopup';
import { selectCurrentUser } from '../../../../store/slices/login/login';

interface MultipleItemsDetailsProps {
  openRightSideBarForMethod: (method: viewRightSideBarEnum) => void;
  reloadTable: () => void;
  selectedItems: ILocOrgItem[];
  setOpenRightSideBar: (open: boolean) => void;
  ownerId?: number;
}
export const MultipleItemsDetails = ({
  openRightSideBarForMethod,
  reloadTable,
  selectedItems,
  setOpenRightSideBar,
  ownerId = undefined
}: MultipleItemsDetailsProps) => {
  const isAllLocations = selectedItems.every(
    (item) => item.type === homeListTypes.location
  );
  const user = useSelector(selectCurrentUser);
  const [deleteOrganization, { isSuccess: isSuccessDeleteOrganization }] =
    useDeleteOrganizationMutation();

  const [
    deleteLocationByRole,
    {
      isSuccess: isSuccessDeleteLocationByRole,
      isError: isErrorDeleteLocationByRole,
      error: errorDeleteLocationByRole
    }
  ] = useDeleteLocationByRoleMutation();

  useEffect(() => {
    if (isErrorDeleteLocationByRole && errorDeleteLocationByRole) {
      if (
        'status' in errorDeleteLocationByRole &&
        'data' in errorDeleteLocationByRole
      ) {
        const errorData = errorDeleteLocationByRole.data as {
          message?: string;
        };
        toast.error(errorData.message || Strings.common.somethingWentWrong);
      } else {
        toast.error(Strings.common.somethingWentWrong);
      }
    }
  }, [isErrorDeleteLocationByRole, errorDeleteLocationByRole]);

  const [resetLocation, { isSuccess: isSuccessResetLocation }] =
    useResetLocationMutation();

  useEffect(() => {
    if (isSuccessResetLocation) {
      toast.success(Strings.locations.resetLocationSuccess);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessResetLocation]);

  useEffect(() => {
    if (isSuccessDeleteLocationByRole) {
      toast.success(Strings.locations.successDeleteLocation);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteLocationByRole]);

  useEffect(() => {
    if (isSuccessDeleteOrganization) {
      toast.success(Strings.organizations.deleteOrganization);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteOrganization]);

  useEffect(() => {
    if (isSuccessDeleteLocationByRole) {
      toast.success(Strings.locations.successDeleteLocation);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteLocationByRole]);

  const deleteLocationOrganization = () => {
    const locationIds = selectedItems
      .filter((item) => item.type === homeListTypes.location)
      .map((loc) => ({
        locationId: loc.id,
        userId: loc.isOwner ? ownerId ?? loc.ownerId : user.userId ?? 0
      }));
    const organizationIds = selectedItems
      .filter((item) => item.type === homeListTypes.organization)
      .map((org) => org.id);
    if (locationIds && locationIds.length) {
      deleteLocationByRole(locationIds);
    }
    if (organizationIds && organizationIds.length) {
      deleteOrganization(organizationIds);
    }
  };

  const handleResetLocation = () => {
    const locationIds = selectedItems
      .filter((item) => item.type === homeListTypes.location)
      .map((loc) => loc.id);
    if (locationIds && locationIds.length) {
      resetLocation(locationIds.map((id) => id.toString()));
    }
  };

  return (
    <StyledContainerItems>
      <SideBarSimpleMethodItem
        name={Strings.locations.transferOwnership}
        action={() => {
          openRightSideBarForMethod(viewRightSideBarEnum.transferLocation);
        }}
        successAction={false}
        textSuccess="success"
      />
      {isAllLocations && (
        <SideBarSimpleMethodItem
          name={Strings.locations.shareLocationsWithTargets}
          action={() => {
            openRightSideBarForMethod(
              viewRightSideBarEnum.shareLocationsWithTargets
            );
          }}
          successAction={false}
          textSuccess="success"
        />
      )}
      <SideBarSimpleMethodItem
        name={Strings.locations.moveToOrg}
        action={() => {
          openRightSideBarForMethod(viewRightSideBarEnum.moveLocation);
        }}
        successAction={false}
        textSuccess="success"
      />
      <SideBarResetActionWithPopup
        onReset={handleResetLocation}
        titleConfirmation={Strings.locations.resetLocation}
        textConfirmation={Strings.common.thisActionIsIrreversible}
        textIcon={Strings.locations.resetLocation}
      />
      <SideBarDeleteActionWithPopup
        onDelete={() => deleteLocationOrganization()}
        titleConfirmation={Strings.locations.deleteLocationTitle}
        textConfirmation={Strings.common.thisActionIsIrreversible}
        textIcon={Strings.common.delete}
      />
    </StyledContainerItems>
  );
};
